<template>
    <div>
        <ContentSection :first="true" :last="true">
            <MainSectionTitle>{{ i18n("views.DiscountEdit.Discount") }}:</MainSectionTitle>

            <div class="form-group row mb-5">
                <FormLabel :required="true">{{ i18n("views.DiscountEdit.Name") }}</FormLabel>
                <FormField>
                    <MultiLanguageInput ref="nameField" v-model="name" />
                </FormField>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.InternalName") }}</FormLabel>
                <FormField>
                    <TextInput ref="internalNameField" v-model="internalName" />
                </FormField>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.Type") }}</FormLabel>
                <FormField>
                    <SelectFromEnumInput
                        v-model="type"
                        :enumDataSource="DiscountType"
                        :disabled="isAlreadyUsed"
                        enumTranslationNamespace="enums.DiscountType"
                    />
                </FormField>
            </div>

            <div class="form-group row" v-if="type === DiscountType.Percentage && percentageDiscountOptions">
                <FormLabel :required="true">{{ i18n("views.DiscountEdit.Value") }}</FormLabel>
                <FormField>
                    <NumberInput
                        v-model="percentageDiscountOptions.value"
                        ref="percentageValueInput"
                        :disabled="isAlreadyUsed || isOpenValue"
                        :options="{
                            min: 0.0001,
                            required: false,
                            step: 0.0001,
                            max: 100,
                            decimals: 4,
                            hideButtons: true,
                        }"
                    />
                </FormField>
            </div>
            <div v-else-if="type === DiscountType.Absolute && absoluteDiscountOptions">
                <div class="form-group row" v-if="type === DiscountType.Absolute && absoluteDiscountOptions">
                    <FormLabel :required="true">{{ i18n("views.DiscountEdit.Value") }}</FormLabel>
                    <FormField>
                        <NumberInput
                            v-model="absoluteDiscountOptions.value"
                            ref="absoluteValueInput"
                            :disabled="isAlreadyUsed || isOpenValue"
                            :options="{
                                min: 0.01,
                                required: false,
                                step: 0.01,
                                decimals: 2,
                                hideButtons: true,
                            }"
                        />
                    </FormField>
                </div>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.OpenValue") }}</FormLabel>
                <FormField>
                    <SwitchInput v-model="isOpenValue" :disabled="isAfterFirstUse" />
                </FormField>
            </div>
            <div class="form-group row" v-if="type === DiscountType.Absolute && absoluteDiscountOptions">
                <FormLabel>{{ i18n("views.DiscountEdit.ApplyOncePerOrderOrBill") }}</FormLabel>
                <FormField>
                    <SwitchInput v-model="absoluteDiscountOptions.applyOncePerOrder" :disabled="isAfterFirstUse" />
                </FormField>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.DiscountCode") }}</FormLabel>
                <FormField>
                    <div class="input-group code-input-group">
                        <TextInput v-model="code" :disabled="true" />
                        <div class="input-group-append" :class="{ hidden: isNew }">
                            <a ref="copyCodeBtnElement" href="#" class="btn btn-secondary copy" @click.prevent>
                                <i class="la la-copy"></i>
                            </a>
                            <a href="#" class="btn btn-secondary qr" @click.prevent="generateQrCode">
                                <i class="la la-qrcode"></i>
                            </a>
                        </div>
                    </div>
                </FormField>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.CustomCode") }}</FormLabel>
                <FormField style="display: flex">
                    <TextInput ref="customCodeField" v-model="customCode" :maxLength="24" />
                    <span class="discount-custom-code-validation-icon" v-if="(customCode?.length ?? 0) > 0">
                        <i
                            v-if="isCustomCodeValid && isCustomCodeAvailable"
                            class="code-is-valid fas fa-check-circle"
                        ></i>
                        <i
                            v-else-if="isCustomCodeValid && isCustomCodeAvailable == null"
                            class="code-is-loading fas fa-spinner fa-spin"
                        ></i>
                        <i v-else class="code-is-invalid fas fa-times-circle"></i>
                    </span>
                </FormField>
            </div>

            <div class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.Order") }}</FormLabel>
                <FormField>
                    <NumberInput
                        v-model="order"
                        :options="{
                            min: 0,
                            step: 1,
                            decimals: 0,
                            hideButtons: false,
                        }"
                    />
                </FormField>
            </div>

            <div v-if="!isNew" class="form-group row">
                <FormLabel>{{ i18n("views.DiscountEdit.Created") }}</FormLabel>
                <FormField>
                    <TextInput v-model="createdDateTime" :disabled="true" />
                </FormField>
            </div>

            <div v-if="!isNew" class="form-group form-group-last row">
                <FormLabel>{{ i18n("views.DiscountEdit.Cancelled") }}</FormLabel>
                <FormField style="display: flex">
                    <TextInput v-model="cancellationDateTime" :disabled="true" />
                    <button
                        v-if="!isCancelled"
                        class="btn btn-danger btn-bold ml-4"
                        type="button"
                        @click="onClickCancelDiscount"
                    >
                        {{ i18n("views.DiscountEdit.CancellationBtn") }}
                    </button>
                </FormField>
            </div>
        </ContentSection>

        <SectionSeparator />
        <ContentSection :last="true">
            <DiscountApplicabilityForm v-if="isInitialized" ref="applicabilityForm" :applicability="applicability" />
        </ContentSection>

        <SectionSeparator />
        <ContentSection :last="true">
            <DiscountUserGroupAccessSettingsForm
                v-if="isInitialized"
                ref="userGroupAccessSettingsForm"
                :userGroupAccessSettings="userGroupAccessSettings"
            />
        </ContentSection>

        <SectionSeparator />
        <ContentSection :last="true">
            <div class="form-group row" v-if="isInitialized">
                <FormLabel :required="true">{{ i18n("views.DiscountEdit.Accounting") }}</FormLabel>
                <FormField>
                    <SelectFromDataSourceInput
                        ref="accountingPaymentMethodSelect"
                        v-model="accountingPaymentMethodId"
                        :dataSource="nonRevenueEffectivePaymentMethodsDataSource"
                        :formatter="formatPaymentMethodName"
                        :lazyInit="false"
                        :keyProp="'id'"
                        :showAllText="i18n('elements.select.pleaseSelect')"
                    />
                </FormField>
            </div>
        </ContentSection>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import type { DaoError, IDataSource } from "@/data/dao/service/BaseServiceDao";
    import { DiscountsDao } from "@/data/dao/service/DiscountsDao";
    import type {
        IAbsoluteDiscountOptions,
        IDiscount,
        IDiscountApplicability,
        IDiscountUserGroupAccessSettings,
        IPercentageDiscountOptions,
    } from "@/data/model/Discount";
    import { DiscountAccessMode, DiscountType } from "@/data/model/Enums";
    import type { ILocationPaymentMethod } from "@/data/model/LocationPaymentMethod";
    import type { IMultiLanguageText } from "@/data/model/Shared";
    import type { ICreateDiscountCmd } from "@/data/queries/discounts/ICreateDiscountCmd";
    import type { IUpdateDiscountCmd } from "@/data/queries/discounts/IUpdateDiscountCmd";
    import { CurrentLocationService } from "@/data/services/CurrentLocationService";
    import { exceptionTypes } from "@/data/util/Config";
    import { DateTime } from "@/data/util/DateTime";
    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import type { VueComponentInstance } from "@/shared/utils/vue";
    import ClipboardJS from "clipboard";
    import { computed, onMounted, ref, watch } from "vue";

    import DiscountApplicabilityForm from "@/portal/components/discounts/DiscountApplicabilityForm.vue";
    import DiscountUserGroupAccessSettingsForm from "@/portal/components/discounts/DiscountUserGroupAccessSettingsForm.vue";
    import MultiLanguageInput from "@/portal/components/elements/MultiLanguageInput.vue";
    import NumberInput from "@/portal/components/elements/NumberInput.vue";
    import SelectFromDataSourceInput from "@/portal/components/elements/SelectFromDataSourceInput.vue";
    import SelectFromEnumInput from "@/portal/components/elements/SelectFromEnumInput.vue";
    import SwitchInput from "@/portal/components/elements/SwitchInput.vue";
    import TextInput from "@/portal/components/elements/TextInput.vue";
    import ContentSection from "@/portal/components/layout/details/ContentSection.vue";
    import FormField from "@/portal/components/layout/details/FormField.vue";
    import FormLabel from "@/portal/components/layout/details/FormLabel.vue";
    import MainSectionTitle from "@/portal/components/layout/details/MainSectionTitle.vue";
    import SectionSeparator from "@/portal/components/layout/details/SectionSeparator.vue";

    const dao = Factory.getInstance<DiscountsDao>(DiscountsDao);

    const isNew = ref<boolean>();
    const id = ref<string | undefined>();
    const locationId = ref<string>(CurrentLocationService.getLocationId()!);
    const type = ref<DiscountType>(DiscountType.Percentage);
    const name = ref<IMultiLanguageText>();
    const internalName = ref<string | undefined>();
    const code = ref<string>("");
    const customCode = ref<string | undefined>();
    const order = ref<number | undefined>();
    const isOpenValue = ref<boolean>(false);
    const isCancelled = ref<boolean>();
    const absoluteDiscountOptions = ref<IAbsoluteDiscountOptions | undefined>();
    const percentageDiscountOptions = ref<IPercentageDiscountOptions | undefined>();
    const applicability = ref<IDiscountApplicability>();
    const userGroupAccessSettings = ref<IDiscountUserGroupAccessSettings>();
    const accountingPaymentMethodId = ref<string | undefined>();
    const createdDateTime = ref<string | undefined>();
    const cancellationDateTime = ref<string | undefined>();
    const isAlreadyUsed = ref<boolean>(false);
    const isLoadingCode = ref<boolean>(true);
    const isInitialized = ref<boolean>(false);
    const timesUsed = ref<number>(0);

    let $saveBtn: JQuery | undefined;

    const formatPaymentMethodName = (locationPaymentMethod: ILocationPaymentMethod) => {
        const paymentMethod = locationPaymentMethod.predefinedPaymentMethod;
        return paymentMethod == null ? locationPaymentMethod.name! : translate(paymentMethod.localizedName);
    };

    const isCustomCodeAvailable = ref<boolean | undefined>();
    const isAfterFirstUse = computed(() => {
        return timesUsed.value > 0;
    });

    const isCustomCodeValid = computed(() => {
        if (customCode.value == null || customCode.value?.length === 0) {
            return true;
        }

        if (customCode.value.length < 4 || customCode.value.length > 24) {
            return false;
        }

        return /^[A-Z0-9]{4,24}$/.test(customCode.value);
    });

    watch(customCode, async () => {
        if (customCode.value?.toLocaleUpperCase() != customCode.value) {
            customCode.value = customCode.value?.toLocaleUpperCase();
        }

        if (isCustomCodeValid.value) {
            isCustomCodeAvailable.value = undefined;

            const currentCustomCode = customCode.value;
            setTimeout(async () => {
                if (isCustomCodeValid.value && currentCustomCode === customCode.value) {
                    const result = await dao.checkCustomDiscountCode({
                        customCode: currentCustomCode!,
                        locationId: locationId.value,
                        discountId: id.value,
                    });

                    if (customCode.value === currentCustomCode) {
                        isCustomCodeAvailable.value = result.isAvailable;
                    }
                }
            }, 500);
        }
    });

    watch(
        () => percentageDiscountOptions?.value,
        () => {
            // percentage discount - min 0.0001, max 100
            if (percentageDiscountOptions.value?.value) {
                if (percentageDiscountOptions.value.value > 100) {
                    percentageDiscountOptions.value!.value = 100;
                } else if (percentageDiscountOptions.value.value < 0.0001) {
                    percentageDiscountOptions.value!.value = 0.0001;
                }
            }
        }
    );

    watch(
        () => isOpenValue.value,
        () => {
            if (isOpenValue.value) {
                if (absoluteDiscountOptions.value?.value) {
                    absoluteDiscountOptions.value!.value = undefined;
                }

                if (percentageDiscountOptions.value?.value) {
                    percentageDiscountOptions.value!.value = undefined;
                }
            }
        }
    );

    watch(
        () => absoluteDiscountOptions?.value,
        () => {
            // absolute discount - min 0.01
            if (absoluteDiscountOptions.value?.value) {
                if (absoluteDiscountOptions.value.value < 0.01) {
                    absoluteDiscountOptions.value!.value = 0.01;
                }
            }
        }
    );

    const nameField = ref<InstanceType<typeof MultiLanguageInput>>();
    const internalNameField = ref<InstanceType<typeof TextInput>>();
    const copyCodeBtnElement = ref<HTMLElement | null>(null);
    const customCodeField = ref<InstanceType<typeof TextInput>>();
    const percentageValueInput = ref<InstanceType<typeof NumberInput> | null>(null);
    const absoluteValueInput = ref<InstanceType<typeof NumberInput> | null>(null);

    const applicabilityForm = ref<VueComponentInstance<typeof DiscountApplicabilityForm>>();
    const userGroupAccessSettingsForm = ref<VueComponentInstance<typeof DiscountUserGroupAccessSettingsForm>>();
    const accountingPaymentMethodSelect = ref<InstanceType<typeof SelectFromDataSourceInput>>();

    const nonRevenueEffectivePaymentMethodsDataSource: IDataSource<ILocationPaymentMethod> = {
        getData: async () => {
            try {
                const items = await dao.getDiscountPaymentMethods();

                return {
                    limit: 0,
                    offset: 0,
                    hasNext: false,
                    hasPrev: false,
                    items: items,
                };
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    async function setFormData(discount?: IDiscount, saveBtn?: JQuery) {
        if (discount) {
            isNew.value = false;
            id.value = discount.id;
            locationId.value = discount.locationId;
            type.value = discount.type;
            name.value = discount.name;
            internalName.value = discount.internalName;
            code.value = discount.code;
            customCode.value = discount.customCode;
            order.value = discount.order;
            isCancelled.value = discount.isCancelled;
            absoluteDiscountOptions.value = discount.absoluteDiscountOptions ?? {
                value: 5,
                applyOncePerOrder: true,
            };
            percentageDiscountOptions.value = discount.percentageDiscountOptions ?? {
                value: 5,
            };
            isOpenValue.value = discount.isOpenValue;

            applicability.value = discount.applicability;
            // todo: to remove after we implement other modes
            if (
                discount.userGroupAccessSettings.endUsers.enabled &&
                discount.userGroupAccessSettings.endUsers.mode != DiscountAccessMode.Code
            ) {
                discount.userGroupAccessSettings.endUsers.mode = DiscountAccessMode.Code;
            }

            // end todo: to remove after we implement other modes
            userGroupAccessSettings.value = discount.userGroupAccessSettings;
            accountingPaymentMethodId.value = discount.accountingPaymentMethodId;
            createdDateTime.value = DateTime.toPreciseDateTimeString(discount.createdDateTime);
            cancellationDateTime.value = discount.cancellationDateTime
                ? DateTime.toPreciseDateTimeString(discount.cancellationDateTime)
                : undefined;
            isAlreadyUsed.value = discount.isAlreadyUsed;
            timesUsed.value = discount.timesUsed;

            isLoadingCode.value = false;
        } else {
            isNew.value = true;
            id.value = undefined;
            locationId.value = CurrentLocationService.getLocationId()!;
            type.value = DiscountType.Percentage;
            name.value = undefined;
            internalName.value = undefined;
            order.value = undefined;
            isCancelled.value = false;
            absoluteDiscountOptions.value = {
                value: 5,
                applyOncePerOrder: true,
            };
            percentageDiscountOptions.value = {
                value: 5,
            };
            applicability.value = undefined;
            userGroupAccessSettings.value = undefined;
            accountingPaymentMethodId.value = undefined;
            createdDateTime.value = undefined;
            cancellationDateTime.value = undefined;
            isAlreadyUsed.value = false;

            try {
                const response = await dao.generateDiscountCode();
                code.value = response.discountCode;
                isLoadingCode.value = false;
            } catch (err) {
                logError(err);
                UiNotification.showException(i18n("general.failedUpdate"));
                code.value = "";
                customCode.value = undefined;
            }
        }

        isInitialized.value = true;
        $saveBtn = saveBtn ?? $saveBtn;
        $saveBtn?.prop("disabled", isCancelled.value);
    }

    async function onClickCancelDiscount() {
        UiNotification.confirmWithOptions({
            message: i18n("discount.cancelDiscountPopupMessage"),
            confirmBtn: translations.general.confirm,
            cancelBtn: translations.general.back,
            confirmed: async () => {
                await cancelDiscount();
            },
        });
    }

    async function cancelDiscount() {
        try {
            const discount = await dao.cancel(id.value!);
            setFormData(discount);
            UiNotification.showSuccess(i18n("general.successfullyUpdated"));
        } catch (err) {
            logError(err);
            UiNotification.showException(i18n("general.failedUpdate"));
        }
    }

    function generateQrCode() {
        dao.downloadQrCode(id.value!);
    }

    function initializeCopyCode() {
        const clipboard = new ClipboardJS(copyCodeBtnElement.value!, {
            text: () => code.value,
        });
        clipboard.on("success", (e) => {
            UiNotification.showSuccess(i18n("discount.discountCode.copiedClipboard"));
            e.clearSelection();
        });
    }

    function validate() {
        let isValid = true;

        if (name.value == null) {
            nameField.value!.setError(true);
            isValid = false;
        } else if (
            name.value?.de === undefined &&
            name.value?.en === undefined &&
            name.value?.fr === undefined &&
            name.value?.it === undefined
        ) {
            nameField.value!.setError(true);
            isValid = false;
        } else if (
            (name.value?.de !== undefined && name.value?.de.length > 80) ||
            (name.value?.fr !== undefined && name.value?.fr.length > 80) ||
            (name.value?.it !== undefined && name.value?.it.length > 80) ||
            (name.value?.en !== undefined && name.value?.en.length > 80)
        ) {
            nameField.value!.setError(true);
            isValid = false;
        }

        if (internalName.value != null && internalName.value.length > 80) {
            internalNameField.value!.setError(true);
            isValid = false;
        }

        if (!isOpenValue.value) {
            if (type.value == DiscountType.Percentage) {
                if (percentageDiscountOptions.value?.value == null) {
                    percentageValueInput.value?.setError(true);
                    isValid = false;
                }
            }

            if (type.value === DiscountType.Absolute) {
                if (absoluteDiscountOptions.value?.value == null) {
                    absoluteValueInput.value?.setError(true);
                    isValid = false;
                }
            }
        }

        // Custom code validation: must be 4-24 chars, uppercase, alphanumeric
        if (customCode.value != null && customCode.value.length > 0) {
            if (customCode.value.length < 4 || customCode.value.length > 24) {
                customCodeField.value!.setError(true);
                isValid = false;
                UiNotification.showException(i18n("views.DiscountEdit.CustomCodeInvalidLength"));
            } else if (!/^[A-Z0-9]{4,24}$/.test(customCode.value)) {
                customCodeField.value!.setError(true);
                isValid = false;
                UiNotification.showException(i18n("views.DiscountEdit.CustomCodeInvalidFormat"));
            } else if (isCustomCodeAvailable.value == false) {
                customCodeField.value!.setError(true);
                isValid = false;
                UiNotification.showException(i18n("views.DiscountEdit.CustomCodeAlreadyTaken"));
            }
        }

        if (!applicabilityForm.value!.validate()) {
            isValid = false;
        }
        if (!userGroupAccessSettingsForm.value!.validate()) {
            isValid = false;
        }

        if (accountingPaymentMethodId.value == null) {
            accountingPaymentMethodSelect.value!.setError(true);
            isValid = false;
        }

        return isValid;
    }

    function getData(): ICreateDiscountCmd | IUpdateDiscountCmd | undefined {
        if (!validate()) {
            return undefined;
        }

        return {
            ...(isNew.value ? {} : { id: id.value! }),
            locationId: locationId.value,
            type: type.value!,
            name: name.value!,
            internalName: internalName.value == null || internalName.value.length == 0 ? undefined : internalName.value,
            code: code.value!,
            customCode: customCode.value == null || customCode.value.length == 0 ? undefined : customCode.value,
            order: order.value,
            absoluteDiscountOptions: type.value === DiscountType.Absolute ? absoluteDiscountOptions.value : undefined,
            percentageDiscountOptions:
                type.value === DiscountType.Percentage ? percentageDiscountOptions.value : undefined,
            applicability: applicabilityForm.value!.getData()!,
            userGroupAccessSettings: userGroupAccessSettingsForm.value!.getData()!,
            accountingPaymentMethodId: accountingPaymentMethodId.value!,
            isOpenValue: isOpenValue.value,
        };
    }

    function resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.discountCustomCodeAlreadyTakenException:
                customCodeField.value!.setError(true);
                return i18n("views.DiscountEdit.CustomCodeAlreadyTaken");
            case exceptionTypes.discountCustomCodeInvalidFormatException:
                customCodeField.value!.setError(true);
                return i18n("views.DiscountEdit.CustomCodeInvalidFormat");
            case exceptionTypes.discountCustomCodeInvalidLengthException:
                customCodeField.value!.setError(true);
                return i18n("views.DiscountEdit.CustomCodeInvalidLength");
        }

        return b.additionalData.reason;
    }

    onMounted(() => {
        initializeCopyCode();
    });

    defineExpose({
        validate,
        getData,
        setFormData,
        resolveErrorMessage,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/colors.scss";

    .discount-custom-code-validation-icon {
        margin-left: 0.75rem;
        font-size: 2rem;
        align-self: center;

        .code-is-valid {
            color: colors.$green;
        }

        .code-is-loading {
            color: colors.$greenHover;
        }

        .code-is-invalid {
            color: colors.$tastierRedPink;
        }
    }

    .code-input-group {
        .code-to-copy-input {
            display: none;
        }

        .input-group-append.hidden {
            display: none;
        }

        .copy,
        .qr {
            i {
                padding-right: 0;
            }
        }
    }
</style>
